import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { uniqBy } from "lodash";
import { Option } from "components/form/CheckboxGroupController";
import { SESSION_KEY } from "constants/key";
import { CostPriceMap, ITireInfo } from "features/mst_proc_fee/types/proc-fee";
import { OptionItem } from "features/mst_option/types/option";
import {
  fetchAllMstItemOptions,
  fetchNatMstItemOptions,
} from "features/mst_option/utils/fetchMstItemOptions";
import getWheelDetailByCodeApi, {
  WheelDetailRequest,
} from "features/mst_wheel/api/wheelCodeSearchApi";
import { MstItemDetailWheel, MstItemDetailWheelDetail } from "features/mst_wheel/types";
import { FormData } from "features/mst_store_tire_wheel_filter/components/FilterOptionModal";
import { tireCategoryText } from "features/mst_proc_fee/const/options";
import { FastenerState } from "features/mst_store_tire_wheel_filter/types/wheel";
import { filterWheelsForStoreList } from "features/mst_store_tire_wheel_filter/utils/wheelListVisibility";
import { getSessionData, setSessionData } from "util/session_action";
import { fetchCostPricesForTires } from "features/mst_proc_fee/api/procFeeApi";
import { TAX_RATE } from "config";
import { formatWheelRimDiamDisplay } from "utils";

// Types for useReducer
type WheelState = {
  selectedWheels: number[];
  wheelDetail: MstItemDetailWheelDetail[];
  makerNames: Option[];
  discountRate: { id: number; value: string; isError: boolean }[];
  sortedIds: number[] | null;
  options: {
    nat: OptionItem[];
    lockNat: OptionItem[];
    hub: OptionItem[];
    wcoating: OptionItem[];
  };
  sortOrder: "asc" | "desc" | null;
  isLoading: boolean;
  // response.list.store.* — drive cost/fetch trigger and per-wheel special-pricing branch.
  priceSettingWheel: number;
  priceSettingWheelLowPrice: number;
};

type WheelAction =
  | { type: "SET_SELECTED_WHEELS"; payload: number[] }
  | { type: "SET_WHEEL_DETAIL"; payload: MstItemDetailWheelDetail[] }
  | {
      type: "SET_WHEEL_DETAIL_DISCOUNT_RATE";
      payload: { id: number; value: string; isError: boolean }[];
    }
  | { type: "SET_SORTED_IDS"; payload: number[] | null }
  | { type: "SET_MAKER_NAMES"; payload: Option[] }
  | {
      type: "SET_OPTIONS";
      payload: {
        nat?: OptionItem[];
        lockNat?: OptionItem[];
        hub?: OptionItem[];
        wcoating?: OptionItem[];
      };
    }
  | { type: "SET_SORT_ORDER"; payload: "asc" | "desc" | null }
  | { type: "SET_LOADING"; payload: boolean }
  | {
      type: "SET_STORE_PRICE_SETTINGS";
      payload: { priceSettingWheel: number; priceSettingWheelLowPrice: number };
    };

const DISABLED_TEXT = "なし" as const;

/** Number of wheels per line item for store quote pricing. */
const WHEEL_QUANTITY = 4;

/** Fallback cost (yen) when `costPriceMap` has no entry for the item (legacy behavior). */
const BASE_PRICE = 10000;

/** Default margin rate (%) when `sale_price_rate_cost` is null. */
const DEFAULT_SALE_PRICE_RATE_COST = 25;

const { floor, ceil: roundUp } = Math;

/** Inputs for wheel regular-price calculation (tax-inclusive unit logic per branch). */
export type WheelRegularPriceParams = {
  low_price_flag: number;
  price_setting_wheel: number | null;
  list_price: number;
  cost_price: number;
  sale_price_rate_cost: number | null;
  /** Low-price + selling setting: tax-inclusive unit from JOIN (rim_diam, width, PCD, store). */
  wheel_selling_price: number;
};

/** Optional behavior for {@link calculateWheelRegularTotalPrice}. */
export type CalculateWheelRegularTotalPriceOptions = {
  /**
   * When true, selling branches (list or `wheel_selling_price`) use pre-tax units; cost branches unchanged.
   * Used as the discount base; display total should still use the default (false).
   */
  sellingPriceExcludeTax?: boolean;
};

/**
 * Cost-based path (shared by normal + low-price when `price_setting_wheel` is 1 or null):
 * base_price = roundUp(cost / (1 - rate/100)), then unit = floor(base_price * TAX_RATE).
 */
function unitPriceFromCostWithMarginAndTax(
  costPrice: number,
  salePriceRateCost: number | null | undefined,
): number {
  const rate = salePriceRateCost ?? DEFAULT_SALE_PRICE_RATE_COST;
  const denominator = 1 - rate / 100;
  if (denominator <= 0) return 0;
  const basePrice = roundUp(costPrice / denominator);
  return floor(basePrice * TAX_RATE);
}

/** Normal (non–low-price) branch when selling from list: tax on list price. */
function unitPriceFromListWithTax(listPrice: number): number {
  return floor(listPrice * TAX_RATE);
}

/** Normal selling from list: body price per wheel (no tax), for discount base. */
function unitPriceFromListExTax(listPrice: number): number {
  return floor(listPrice);
}
/**
 * Total regular price for four wheels from explicit params (business rules).
 * Branches: `low_price_flag`, `price_setting_wheel` (0 = from selling / list path, 1|null = from cost).
 * @param params - Branch inputs
 * @param options - When `sellingPriceExcludeTax` is true, branches 2.1 / 3.1 use ex-tax units; cost paths unchanged.
 */
export function calculateWheelRegularTotalPrice(
  params: WheelRegularPriceParams,
  options?: CalculateWheelRegularTotalPriceOptions,
): number {
  const {
    low_price_flag,
    price_setting_wheel,
    list_price,
    cost_price,
    sale_price_rate_cost,
    wheel_selling_price,
  } = params;

  const sellingPriceExcludeTax = options?.sellingPriceExcludeTax ?? false;
  const isLowPrice = low_price_flag === 1;
  /** `0` = price from selling (list or wheel_selling_price); `1` or `null` = from cost + margin. */
  const fromSellingPriceSetting = price_setting_wheel === 0;

  if (!isLowPrice) {
    if (fromSellingPriceSetting) {
      // 2.1 — Normal: list (+ tax when display), ×4
      const unitPrice = sellingPriceExcludeTax
        ? unitPriceFromListExTax(list_price)
        : unitPriceFromListWithTax(list_price);
      return unitPrice * WHEEL_QUANTITY;
    }
    // 2.2 — Normal: cost + margin + tax, ×4
    const unitPrice = unitPriceFromCostWithMarginAndTax(cost_price, sale_price_rate_cost);
    return unitPrice * WHEEL_QUANTITY;
  }

  if (fromSellingPriceSetting) {
    // 3.1 — Low price: wheel_selling_price already includes tax, ×4
    const unitPrice = wheel_selling_price;
    return unitPrice * WHEEL_QUANTITY;
  }

  // 3.2 — Low price: same cost formula as 2.2, ×4
  const unitPrice = unitPriceFromCostWithMarginAndTax(cost_price, sale_price_rate_cost);
  return unitPrice * WHEEL_QUANTITY;
}

const initialWheelState: WheelState = {
  selectedWheels: [],
  wheelDetail: [],
  makerNames: [],
  discountRate: [],
  sortedIds: null,
  options: { nat: [], lockNat: [], hub: [], wcoating: [] },
  sortOrder: null,
  isLoading: false,
  priceSettingWheel: 0,
  priceSettingWheelLowPrice: 0,
};

/**
 * Find the price of an option
 * @param list - The list of options
 * @param selected - The selected option
 * @returns The price of the option
 */
export const findOptionPrice = (list: OptionItem[], selected: string): number => {
  if (!selected || selected === DISABLED_TEXT) return 0;
  const numericId = Number(selected);
  let item = Number.isNaN(numericId) ? undefined : list.find((el) => el.id === numericId);
  if (!item) {
    item = list.find((el) => el.option_cd === selected);
  }
  return item?.sale_price ?? 0;
};

/**
 * Regular line total (4 wheels) for a wheel row, using store cost map when present.
 * Delegates to {@link calculateWheelRegularTotalPrice} (single source of business rules).
 */
export const caclulateRegularPrice = (
  item: MstItemDetailWheel,
  costPriceMap?: CostPriceMap,
): number => {
  const costFromMap = costPriceMap?.[item.item_cd];
  const cost_price = costFromMap !== undefined ? Number(costFromMap) : BASE_PRICE;

  return calculateWheelRegularTotalPrice({
    low_price_flag: item.low_price_flag ?? 0,
    price_setting_wheel: item.price_setting_wheel,
    list_price: item.list_price ?? 0,
    cost_price,
    sale_price_rate_cost: item.sale_price_rate_cost ?? null,
    wheel_selling_price: item.wheel_selling_price ?? 0,
  });
};

/**
 * Pre-discount line total (4 wheels) used only for {@link calculateDiscountedPrice}.
 * When `price_setting_wheel === 0` (selling path), list / wheel selling use ex-tax units; cost path matches display total.
 */
export const caclulateRegularPriceForDiscount = (
  item: MstItemDetailWheel,
  costPriceMap?: CostPriceMap,
): number => {
  const costFromMap = costPriceMap?.[item.item_cd];
  const cost_price = costFromMap !== undefined ? Number(costFromMap) : BASE_PRICE;
  const fromSellingPriceSetting = item.price_setting_wheel === 0;

  return calculateWheelRegularTotalPrice(
    {
      low_price_flag: item.low_price_flag ?? 0,
      price_setting_wheel: item.price_setting_wheel,
      list_price: item.list_price ?? 0,
      cost_price,
      sale_price_rate_cost: item.sale_price_rate_cost ?? null,
      wheel_selling_price: item.wheel_selling_price ?? 0,
    },
    { sellingPriceExcludeTax: fromSellingPriceSetting },
  );
};

/**
 * Calculate the discounted price of a wheel
 * @param item - The wheel item
 * @param regularPrice - Pre-discount line total (typically from {@link caclulateRegularPriceForDiscount})
 * @param discountRate - The discount rate of the wheel
 * @returns The discounted price of the wheel
 */
export const calculateDiscountedPrice = (
  item: MstItemDetailWheel,
  regularPrice: number,
  discountRate: number,
): number => {
  const { fixed_discount } = item;
  if (!regularPrice) return 0;

  const effectiveDiscount = Number(discountRate) || fixed_discount || 0;

  return Math.max(0, Math.round(regularPrice * (1 - effectiveDiscount / 100)));
};

/**
 * Calculate the price of a wheel
 * @param item - The wheel item
 * @param discountRate - The discount rate of the wheel
 * @param optionsPrice - The price of the options
 * @param costPriceMap - Optional map of item_cd to cost_price from API
 * @returns The price of the wheel
 */
export const calculateWheelPrice = (
  item: MstItemDetailWheel,
  discountRate: number,
  optionsPrice: number,
  costPriceMap?: CostPriceMap,
): MstItemDetailWheelDetail => {
  const regularPrice = caclulateRegularPrice(item, costPriceMap);
  const regularPriceForDiscount = caclulateRegularPriceForDiscount(item, costPriceMap);
  const discountedPrice = calculateDiscountedPrice(item, regularPriceForDiscount, discountRate);
  return {
    ...item,
    regular_price: regularPrice,
    discounted_price: discountedPrice,
    final_price: discountedPrice + optionsPrice,
  };
};

// Reducer for managing wheel-related state
const wheelReducer = (state: WheelState, action: WheelAction): WheelState => {
  switch (action.type) {
    case "SET_WHEEL_DETAIL":
      return { ...state, wheelDetail: action.payload, isLoading: false };
    case "SET_SELECTED_WHEELS":
      return { ...state, selectedWheels: action.payload };
    case "SET_WHEEL_DETAIL_DISCOUNT_RATE":
      return { ...state, discountRate: action.payload };
    case "SET_SORTED_IDS":
      return { ...state, sortedIds: action.payload };
    case "SET_MAKER_NAMES":
      return { ...state, makerNames: action.payload };
    case "SET_OPTIONS":
      return {
        ...state,
        options: {
          nat: action.payload.nat ?? state.options.nat,
          lockNat: action.payload.lockNat ?? state.options.lockNat,
          hub: action.payload.hub ?? state.options.hub,
          wcoating: action.payload.wcoating ?? state.options.wcoating,
        },
      };
    case "SET_SORT_ORDER":
      return { ...state, sortOrder: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_STORE_PRICE_SETTINGS":
      return {
        ...state,
        priceSettingWheel: action.payload.priceSettingWheel,
        priceSettingWheelLowPrice: action.payload.priceSettingWheelLowPrice,
      };
    default:
      return state;
  }
};

/**
 * Custom hook to retrieve session storage data for tire category and selected car.
 * @returns An object containing:
 *   - tireCategory: The selected tire category from session storage.
 *   - selectedCar: The selected car object parsed from session storage.
 */
const useSessionData = () =>
  useMemo(() => {
    const tireCategory = sessionStorage.getItem(SESSION_KEY.TIRE_TYPE);
    const selectedCar = JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_MAKER) || "{}");
    const tireSize: ITireInfo[] =
      JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_TIRE) || "[]") ?? [];
    return { tireCategory, selectedCar, tireSize };
  }, []);

/**
 * Custom hook to compute and memoize selected car information for display.
 * @param selectedCar - The selected car object from session storage.
 * @param tireCategory - The selected tire category string or null.
 * @returns An object containing:
 *   - image: The car image URL.
 *   - model: JSX element or string representing the car model and period.
 *   - type: The display text for the tire category.
 *   - size: The car tire size string.
 *   - specialSize: Hub bore (ハブ径) from `selectedTire.mmm_hub1` in session.
 */
const useSelectedCarInfo = (selectedCar: any, tireCategory: string | null) => {
  const renderSelectedCar = useMemo(() => {
    if (Object.keys(selectedCar).length === 0) return "";
    const { car_model_and_period, modl_name } = selectedCar;
    if (!car_model_and_period || !modl_name) return "";

    if (car_model_and_period.includes(modl_name)) {
      const periodPart = car_model_and_period.replace(modl_name, "").trim();
      return (
        <>
          {modl_name} <br />
          {periodPart}
        </>
      );
    }
    return car_model_and_period;
  }, [selectedCar?.car_model_and_period, selectedCar?.modl_name]);

  return useMemo(
    () => ({
      image: selectedCar?.car_image_url || "",
      model: renderSelectedCar,
      type: tireCategoryText(tireCategory || ""),
      size: selectedCar?.selectedTire?.mmm_car_tiresize || "",
      specialSize: formatWheelRimDiamDisplay(selectedCar?.selectedTire?.mmm_hub1 || ""),
    }),
    [selectedCar, renderSelectedCar, tireCategory],
  );
};

/**
 * Interface for wheel store state saved to session storage
 */
interface WheelStoreState {
  selectedWheels: number[];
  discountRate: { id: number; value: string; isError: boolean }[];
  fasteners: FastenerState;
  paramReq: WheelDetailRequest;
  sortOrder: "asc" | "desc" | null;
  sortedIds: number[] | null;
}

/**
 * Custom hook for managing quote wheel functionality
 * Handles wheel data fetching, sorting, filtering, and related state management
 */
const useQuoteWheel = () => {
  const { tireCategory, selectedCar, tireSize } = useSessionData();
  const selectedCarInfo = useSelectedCarInfo(selectedCar, tireCategory);

  // Use reducer for complex state management
  const [state, dispatch] = useReducer(wheelReducer, initialWheelState);

  // Cost Price Map for special pricing
  const [costPriceMap, setCostPriceMap] = useState<CostPriceMap>({});

  /** Which wheel row has the special-discount input focused (keeps row visible while editing). */
  const [discountFocusWheelId, setDiscountFocusWheelId] = useState<number | null>(null);

  // Ref to track current state for callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Track if component has mounted
  const hasMounted = useRef(false);

  /** After first full option load, lock-nut changes only refetch ナット (mode nat). */
  const optionsInitializedRef = useRef(false);

  // Load saved state from session storage
  const savedState = useMemo(() => {
    const saved = getSessionData<WheelStoreState>(SESSION_KEY.WHEEL_STORE_STATE);
    return saved;
  }, []);

  const buildDefaultParamReq = useCallback(
    (): WheelDetailRequest => ({
      item_cd: "",
      wheel_width: String(selectedCar?.selectedTire?.mmm_wheel_width || ""),
      wheel_inset_from: String(selectedCar?.selectedTire?.mmm_inset_from || ""),
      wheel_inset_to: String(selectedCar?.selectedTire?.mmm_inset_to || ""),
      wheel_pcd: String(selectedCar?.selectedTire?.mmm_pcd || ""),
      wheel_hole: String(selectedCar?.selectedTire?.mmm_hole || ""),
      tire_rim_diam:
        String(selectedCar?.selectedTire?.mmm_inch || tireSize[0]?.tire_rim_diam || "") ||
        undefined,
      limit: 9999,
      offset: 1,
    }),
    [selectedCar, tireSize],
  );

  // Only keep param request as useState since it's frequently updated
  const [paramReq, setParamReq] = useState<WheelDetailRequest>(() => {
    const defaultParams = buildDefaultParamReq();
    if (savedState?.paramReq) {
      return {
        ...defaultParams,
        ...savedState.paramReq,
        wheel_inset_from:
          savedState.paramReq.wheel_inset_from ?? defaultParams.wheel_inset_from,
        wheel_inset_to: savedState.paramReq.wheel_inset_to ?? defaultParams.wheel_inset_to,
      };
    }
    return defaultParams;
  });

  const [fasteners, setFasteners] = useState<FastenerState>(() => {
    // First, try to get from saved wheel store state
    if (savedState?.fasteners) {
      return savedState.fasteners;
    }

    // Second, try to get from SELECTED_WHEEL (from confirm screen)
    try {
      const selectedWheelRaw = sessionStorage.getItem(SESSION_KEY.SELECTED_WHEEL);
      if (selectedWheelRaw) {
        const selectedWheel = JSON.parse(selectedWheelRaw);
        if (selectedWheel.cost_setting) {
          return {
            nut: selectedWheel.cost_setting.nut || "",
            nut_flag: selectedWheel.cost_setting.nut_flag ?? true,
            lock_nut: selectedWheel.cost_setting.lock_nut || "",
            lock_nut_flag: selectedWheel.cost_setting.lock_nut_flag ?? false,
            hub_ring: selectedWheel.cost_setting.hub_ring || "",
            hub_ring_flag: selectedWheel.cost_setting.hub_ring_flag ?? false,
            wheel_coating: selectedWheel.cost_setting.wheel_coating || "",
            wheel_coating_flag: selectedWheel.cost_setting.wheel_coating_flag ?? false,
          };
        }
      }
    } catch (error) {
      // Silently handle error
    }

    // Default values
    return {
      nut: "",
      nut_flag: true,
      lock_nut: "",
      lock_nut_flag: false,
      hub_ring: "",
      hub_ring_flag: false,
      wheel_coating: "",
      wheel_coating_flag: false,
    };
  });

  // Memoized car info for display
  const carInfo = useMemo(
    () => [
      { label: "選択車両", value: selectedCarInfo.model },
      { label: "タイヤ種類", value: selectedCarInfo.type },
      { label: "タイヤサイズ", value: selectedCarInfo.size },
      { label: "ハブ径", value: selectedCarInfo.specialSize },
    ],
    [selectedCarInfo],
  );

  // Stable sorting: keep a sorted id order that updates only when sort toggles
  const sortedWheels = useMemo(() => {
    const { wheelDetail, sortOrder, sortedIds } = state;
    if (!sortOrder) return wheelDetail;
    const byId = new Map<number, MstItemDetailWheelDetail>(wheelDetail.map((w) => [w.id, w]));
    const ids = sortedIds && sortedIds.length > 0 ? sortedIds : wheelDetail.map((w) => w.id);
    return ids.map((id) => byId.get(id)!).filter(Boolean);
  }, [state.wheelDetail, state.sortOrder, state.sortedIds]);

  const visibleWheelDetail = useMemo(
    () =>
      sortedWheels.length === 0
        ? []
        : filterWheelsForStoreList(sortedWheels, state.discountRate, discountFocusWheelId),
    [sortedWheels, state.discountRate, discountFocusWheelId],
  );

  const visibleWheelCount = visibleWheelDetail.length;

  useEffect(() => {
    if (sortedWheels.length === 0) {
      setDiscountFocusWheelId(null);
    }
  }, [sortedWheels.length]);

  const optionsPrice: number = useMemo(() => {
    let total = 0;

    if (fasteners.nut_flag) {
      total += findOptionPrice(state.options.nat, fasteners.nut);
    }

    if (fasteners.hub_ring_flag) {
      total += findOptionPrice(state.options.hub, fasteners.hub_ring);
    }

    if (fasteners.lock_nut_flag) {
      total += findOptionPrice(state.options.lockNat, fasteners.lock_nut);
    }

    if (fasteners.wheel_coating_flag) {
      total += findOptionPrice(state.options.wcoating, fasteners.wheel_coating);
    }

    return total;
  }, [fasteners, state.options]);

  // Fetch cost prices for wheels matching either store-level trigger:
  //   (a) store.price_setting_wheel === 1                                              → all wheels
  //   (b) wheel.low_price_flag === 1 && store.price_setting_wheel_low_price === 1      → that wheel
  useEffect(() => {
    const loadCostPrices = async () => {
      if (state.wheelDetail.length === 0) {
        setCostPriceMap({});
        return;
      }
      const store_cd = localStorage.getItem("shop_cd");
      if (!store_cd) return;

      const wheelsForFetch = state.wheelDetail
        .filter((wheel) => {
          const isLowPriceMatch =
            wheel.low_price_flag === 1 && state.priceSettingWheelLowPrice === 1;
          return state.priceSettingWheel === 1 || isLowPriceMatch;
        })
        .map((wheel) => ({ item_cd: wheel.item_cd }));

      if (wheelsForFetch.length === 0) {
        setCostPriceMap({});
        return;
      }

      const map = await fetchCostPricesForTires(wheelsForFetch, store_cd);
      setCostPriceMap(map);
    };

    loadCostPrices();
  }, [state.wheelDetail.length, state.priceSettingWheel, state.priceSettingWheelLowPrice]);

  // API functions
  const fetchWheelDetail = useCallback(
    async (paramsReq: WheelDetailRequest) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await getWheelDetailByCodeApi(paramsReq);
        const uniqueWheels = uniqBy(response.list.mst_item_detail_wheel, "id");

        // Store-level flags now drive the cost-based pricing branch, replacing
        // the old per-item price_setting_wheel semantics.
        const priceSettingWheel = response.list?.store?.price_setting_wheel ?? 0;
        const priceSettingWheelLowPrice = response.list?.store?.price_setting_wheel_low_price ?? 0;
        dispatch({
          type: "SET_STORE_PRICE_SETTINGS",
          payload: { priceSettingWheel, priceSettingWheelLowPrice },
        });

        // Overwrite each wheel's price_setting_wheel so downstream calc branches
        // reflect the new store-level rules: cost path when either
        //   (a) store.price_setting_wheel === 1, or
        //   (b) wheel.low_price_flag === 1 && store.price_setting_wheel_low_price === 1.
        const wheelsWithPriceSetting = uniqueWheels.map((wheel) => {
          const isLowPriceCostPath =
            wheel.low_price_flag === 1 && priceSettingWheelLowPrice === 1;
          const useCostPath = priceSettingWheel === 1 || isLowPriceCostPath;
          return { ...wheel, price_setting_wheel: useCostPath ? 1 : 0 };
        });

        const caculatedPrice = wheelsWithPriceSetting.map((el) =>
          calculateWheelPrice(el, 0, optionsPrice, costPriceMap),
        );
        dispatch({ type: "SET_WHEEL_DETAIL", payload: caculatedPrice });

        // Preserve existing discount rates when wheels change (due to filtering)
        const currentDiscountRates = stateRef.current.discountRate;
        const newDiscountRates = caculatedPrice.map((el) => {
          const existingRate = currentDiscountRates.find((dr) => dr.id === el.id);
          if (existingRate) {
            return existingRate;
          }
          return { id: el.id, value: "", isError: false };
        });

        dispatch({
          type: "SET_WHEEL_DETAIL_DISCOUNT_RATE",
          payload: newDiscountRates,
        });

        // Only set maker names if not already set
        if (state.makerNames.length === 0) {
          const makerOptions = response.list.maker_list.map((maker) => ({
            label: maker.maker_name.trim(),
            value: maker.maker_name.trim(),
          }));
          dispatch({ type: "SET_MAKER_NAMES", payload: makerOptions });
        }
      } catch (error) {
        dispatch({ type: "SET_LOADING", payload: false });
        // Handle error silently or use proper error handling
      }
    },
    [state.makerNames.length, optionsPrice],
  );

  // Action handlers
  const onHandleFilter = useCallback((data: FormData) => {
    setParamReq((prev) => ({
      ...prev,
      maker_names: data.maker_names ?? [],
      wheel_color_names: data.wheel_color_names ?? [],
      winter_flag: data.winter_flag ?? [],
    }));
  }, []);

  const onHandleSelectedWheels = useCallback((data: number[]) => {
    dispatch({ type: "SET_SELECTED_WHEELS", payload: data });
  }, []);

  const setDiscountRate = useCallback((id: number, value: string) => {
    dispatch({
      type: "SET_WHEEL_DETAIL_DISCOUNT_RATE",
      payload: stateRef.current.discountRate.map((el) =>
        el.id === id ? { ...el, value, isError: Number(value) > 100 || Number(value) < 0 } : el,
      ),
    });
  }, []);

  // Toggle sort by ホイール通常4本売価 (regular_price): ASC <-> DESC
  // Display order when sorting uses sortedIds; reducer wheelDetail keeps API/original order (never reordered by sort).
  const toggleSortOrder = useCallback(() => {
    const nextOrder = state.sortOrder === "asc" ? "desc" : "asc";
    const idWithPrice = state.wheelDetail.map((w) => ({
      id: w.id,
      price: w.regular_price ?? 0,
    }));
    const sorted = [...idWithPrice].sort((a, b) =>
      nextOrder === "asc" ? a.price - b.price : b.price - a.price,
    );
    dispatch({ type: "SET_SORTED_IDS", payload: sorted.map((x) => x.id) });
    dispatch({ type: "SET_SORT_ORDER", payload: nextOrder });
  }, [state.sortOrder, state.wheelDetail]);

  /** おすすめ順: clear price sort so list matches original reducer order (clone path via sortedWheels → wheelDetail). */
  const resetRecommendOrder = useCallback(() => {
    dispatch({ type: "SET_SORT_ORDER", payload: null });
    dispatch({ type: "SET_SORTED_IDS", payload: null });
  }, []);

  // Options: first load = all modes; ロックナット変更後は ナット (nat) のみ再取得
  useEffect(() => {
    let cancelled = false;
    const fastenersInput = {
      lock_nut_flag: fasteners.lock_nut_flag,
      lock_nut: fasteners.lock_nut,
    };

    const run = async () => {
      try {
        if (!optionsInitializedRef.current) {
          const list = await fetchAllMstItemOptions(selectedCar, fastenersInput);
          if (cancelled) return;
          optionsInitializedRef.current = true;
          dispatch({
            type: "SET_OPTIONS",
            payload: {
              nat: list.nat,
              lockNat: list.locknat,
              hub: list.hub,
              wcoating: list.wcoating,
            },
          });
          return;
        }

        const {
          options: { lockNat },
        } = stateRef.current;
        const nat = await fetchNatMstItemOptions(selectedCar, fastenersInput, lockNat);
        if (cancelled) return;
        dispatch({ type: "SET_OPTIONS", payload: { nat } });
      } catch {
        // Handle error silently or use proper error handling
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedCar, fasteners.lock_nut_flag, fasteners.lock_nut]);

  useEffect(() => {
    fetchWheelDetail(paramReq);
  }, [paramReq]);

  // Restore saved state after wheel details are loaded
  useEffect(() => {
    if (!hasMounted.current && state.wheelDetail.length > 0 && state.discountRate.length > 0) {
      // Priority 1: Restore from WHEEL_STORE_STATE if available (most recent from this screen)
      // Priority 2: Restore from SELECTED_WHEEL if no WHEEL_STORE_STATE (from confirm screen or prev navigation)

      const shouldRestoreFromWheelStore =
        savedState && savedState.selectedWheels && savedState.selectedWheels.length > 0;

      if (shouldRestoreFromWheelStore) {
        // Restore from WHEEL_STORE_STATE (most recent state from this screen)
        dispatch({ type: "SET_SELECTED_WHEELS", payload: savedState.selectedWheels });

        // Restore discount rates
        if (savedState.discountRate && savedState.discountRate.length > 0) {
          const currentDiscountRates = stateRef.current.discountRate;
          const mergedDiscountRates = currentDiscountRates.map((currentDr) => {
            const savedDr = savedState.discountRate.find((dr) => dr.id === currentDr.id);
            if (savedDr) {
              return {
                id: currentDr.id,
                value: savedDr.value,
                isError: savedDr.isError || false,
              };
            }
            return currentDr;
          });
          dispatch({ type: "SET_WHEEL_DETAIL_DISCOUNT_RATE", payload: mergedDiscountRates });
        }

        // Restore sort order and sorted IDs
        if (savedState.sortOrder) {
          dispatch({ type: "SET_SORT_ORDER", payload: savedState.sortOrder });
        }
        if (savedState.sortedIds && savedState.sortedIds.length > 0) {
          // Validate that saved sorted IDs still exist in current wheel data
          const currentWheelIds = stateRef.current.wheelDetail.map((w) => w.id);
          const validSortedIds = savedState.sortedIds.filter((id) => currentWheelIds.includes(id));

          if (validSortedIds.length > 0) {
            dispatch({ type: "SET_SORTED_IDS", payload: validSortedIds });
          }
        }
      } else {
        // Fallback: Restore from SELECTED_WHEEL if no WHEEL_STORE_STATE
        try {
          const selectedWheelRaw = sessionStorage.getItem(SESSION_KEY.SELECTED_WHEEL);
          if (selectedWheelRaw) {
            const selectedWheel = JSON.parse(selectedWheelRaw);
            if (selectedWheel.wheels_detail && selectedWheel.wheels_detail.length > 0) {
              const wheelIds = selectedWheel.wheels_detail.map((w: any) => w.id);
              dispatch({ type: "SET_SELECTED_WHEELS", payload: wheelIds });

              // Restore discount rates from SELECTED_WHEEL
              if (selectedWheel.discount_rate && selectedWheel.discount_rate.length > 0) {
                const currentDiscountRates = stateRef.current.discountRate;
                const mergedDiscountRates = currentDiscountRates.map((currentDr) => {
                  const savedDr = selectedWheel.discount_rate.find(
                    (dr: any) => dr.id === currentDr.id,
                  );
                  if (savedDr) {
                    return {
                      id: currentDr.id,
                      value: savedDr.value,
                      isError: savedDr.isError || false,
                    };
                  }
                  return currentDr;
                });
                dispatch({ type: "SET_WHEEL_DETAIL_DISCOUNT_RATE", payload: mergedDiscountRates });
              }
            }
          }
        } catch (error) {
          // Silently handle error
        }
      }

      hasMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedState, state.wheelDetail.length]);

  useEffect(() => {
    const caculatedPrice = state.wheelDetail.map((el) => {
      const discountItem = state.discountRate.find((dr) => dr.id === el.id);
      const discountValue = Number(discountItem?.value ?? 0);
      return calculateWheelPrice(el, discountValue, optionsPrice, costPriceMap);
    });
    dispatch({ type: "SET_WHEEL_DETAIL", payload: caculatedPrice });
    // Keep sorted id list length in sync when data length changes
    if (state.sortedIds && state.sortedIds.length !== caculatedPrice.length) {
      dispatch({ type: "SET_SORTED_IDS", payload: caculatedPrice.map((w) => w.id) });
    }
  }, [state.discountRate, optionsPrice, costPriceMap]);

  // Save state to session storage when it changes
  useEffect(() => {
    if (!hasMounted.current && state.wheelDetail.length === 0) {
      // Don't save on initial mount before data is loaded
      return;
    }

    const stateToSave: WheelStoreState = {
      selectedWheels: state.selectedWheels,
      discountRate: state.discountRate,
      fasteners,
      paramReq,
      sortOrder: state.sortOrder,
      sortedIds: state.sortedIds,
    };

    setSessionData(SESSION_KEY.WHEEL_STORE_STATE, stateToSave);
  }, [
    state.selectedWheels,
    state.discountRate,
    fasteners,
    paramReq,
    state.sortOrder,
    state.sortedIds,
    state.wheelDetail.length,
  ]);

  return {
    // State
    paramReq,
    carImage: selectedCarInfo.image,
    /** ホイール一覧の Φ 表示用 — session `NEW_QUOTE_MAKER` → `selectedTire.mmm_hub1` */
    hubRimDisplay: selectedCarInfo.specialSize,
    carInfo,
    options: state.options,
    wheelDetail: sortedWheels,
    visibleWheelDetail,
    visibleWheelCount,
    sortOrder: state.sortOrder,
    makerNames: state.makerNames,
    isLoading: state.isLoading,
    discountRate: state.discountRate,
    selectedWheels: state.selectedWheels,
    fasteners,
    setFasteners,
    optionsPrice,
    sortedIds: state.sortedIds,
    costPriceMap,
    setDiscountFocusWheelId,
    // Actions
    toggleSortOrder,
    resetRecommendOrder,
    onHandleFilter,
    onHandleSelectedWheels,
    setDiscountRate,
  };
};

export { useQuoteWheel, useSelectedCarInfo, useSessionData };
